import React, { useEffect, useMemo, useRef, useState } from 'react';

import { widgetComponents } from 'config/widgetConfigurations/widgetComponents';
import { useParams } from 'react-router-dom';
import { emitHover } from 'services/eventBus/hoverBus';
import { WidgetView } from 'utilitys/reporting/widgetViewModeUtils';

import { useCustomLatestCompareToVersions } from 'hooks/useCustomLatestCompareToVersions';
import useUserHook from 'hooks/useUserHook';

import { useConfigurationStore } from 'store/configurationsStore';

import ErrorBoundary from 'components/common/ErrorBoundary/ErrorBoundary';
import { MuiIcon } from 'components/common/Icons/base/MuiIcon/MuiIcon';

import { PinboardOrReportWidgetWrapperProvider } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetWrapper/context/widgetWrapperContext';
import { useNarrative } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetWrapper/hooks/useNarrative';
import { NoPermissionWidget } from 'components/Dashboards/Project/Components/CustomDashboard/noPermissionWidget';
import { IHashmap } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { WidgetButton } from 'components/Dashboards/Reporting/_common/widgetButtons/WidgetButton';
import { getSizeByDimensions } from 'components/Dashboards/Reporting/reporting.utils';
import { useAddAutomatedNarrativeWidgetToReport } from 'components/Dashboards/Reporting/threeDotsMenu/hooks/useAddAutomatedNarrativeWidgetToReport';
import { ButtonsSeparator } from 'components/Dashboards/Reporting/widgetInReport/components/buttonsSeparator/ButtonsSeparator';
import { SelectedProjectContractView } from 'components/Dashboards/Reporting/widgetInReport/components/selectedProjectContractView/SelectedProjectContractView';
import { WidgetButtonsWrapper } from 'components/Dashboards/Reporting/widgetInReport/components/widgetButtonsWrapper/WidgetButtonsWrapper';
import { WidgetDataDevMode } from 'components/Dashboards/Reporting/widgetInReport/components/widgetDataDevMode/WidgetDataDevMode';
import { WidgetErrorFallback } from 'components/Dashboards/Reporting/widgetInReport/components/widgetErrorFallback/WidgetErrorFallback';
import { useRefreshWidgetPayload } from 'components/Dashboards/Reporting/widgetInReport/hooks/RefreshWidgetPayload/useRefreshWidgetPayload';
import { useHighlightNarrativeRelationOnHover } from 'components/Dashboards/Reporting/widgetInReport/hooks/highlightNarrativeRelationOnHover/useHighlightNarrativeRelationOnHover';
import { useGetProjectAndContractById } from 'components/Dashboards/Reporting/widgetInReport/hooks/useGetProjectAndContractById';
import { Button } from 'components/DesignSystem/Button';
import { ReportPageUrlParams } from 'components/pages/ReportPage/ReportPage';
import { NarrativeWidgetsRelationsMapType } from 'components/pages/ReportPage/hooks/useNarrativesMaps';

import { ReportWidgetRef, Widget, WidgetSizes } from '../reporting.types';
import classes from './WidgetInReport.module.scss';

interface Props {
    widget: Widget;
    projectHierarchyList: IProjectHierarchyList[];
    onEdit?: () => void;
    onDelete?: () => void;
    componentGridSize: { h: number; w: number };
    changeWidgetSize: (widgetId: string, widgetSize: WidgetSizes) => void;
    narrativeWidgetsRelationsMap: NarrativeWidgetsRelationsMapType;
    isFullscreen?: boolean;
}

export const WidgetInReport = ({
    widget,
    onEdit,
    onDelete,
    projectHierarchyList,
    changeWidgetSize,
    componentGridSize,
    narrativeWidgetsRelationsMap,
    isFullscreen = false,
}: Props) => {
    const widgetInReportContainerRef = useRef<HTMLDivElement | null>(null);
    const reportWidgetRef = useRef<ReportWidgetRef | null>(null);
    const reportId = Number(useParams<ReportPageUrlParams>().reportId);
    const [isPayloadReady, setIsPayloadReady] = useState(false);
    const [isNarrativeEdit, setIsNarrativeEdit] = useState<boolean>(false);
    const handleClickNarrative = () => setIsNarrativeEdit(!isNarrativeEdit);
    const { ability, email } = useUserHook();
    const configuration = useConfigurationStore();

    const { project, contract } = useGetProjectAndContractById({
        projectId: widget.projectId,
        contractId: widget.contractId,
        projectHierarchyList,
    });

    const componentObj = useMemo(() => {
        return Boolean(widget.componentKey) ? configuration[widget.componentKey as keyof IHashmap] : null;
    }, [configuration, widget.componentKey]);
    if (!componentObj) return null;
    const { NoPermissionMessage, componentMenu } = componentObj;
    const { Component: Widget } = widgetComponents[widget.componentKey];

    // Widget state persisted in backend (JSON in widget.params); used as the widget’s initial state.
    const widgetState: string | null = useMemo(() => {
        return widget.params ? JSON.parse(widget.params) : null;
    }, [widget.params]);

    // NOTE: In reports, widgets compare latest vs. baseline version IDs (or latest vs. previous for certain widgets)
    const { latestVersionId, compareVersionId, latestVersionTitle, compareVersionTitle } =
        useCustomLatestCompareToVersions({
            projectId: widget.projectId,
            contractId: widget.contractId,
            componentKey: widget.componentKey as keyof IHashmap,
        });

    const { setWidgetPayload } = useRefreshWidgetPayload({ widget, reportWidgetRef });

    const { isGenerativeAi, setIsGenerativeAi, isLoading } = useNarrative({
        email,
        widgetId: widget.id.toString(),
        showGenerativeNarrative: widget.showNarrative ?? false,
        description: widget.description,
    });

    useHighlightNarrativeRelationOnHover({
        containerRef: widgetInReportContainerRef,
        widgetId: widget.id,
        relationsMap: narrativeWidgetsRelationsMap,
    });

    const { onAddAutomatedNarrative, creatingAutomatedNarrativeIsInProgress } = useAddAutomatedNarrativeWidgetToReport({
        parentComponentKey: widget.componentKey,
    });

    const shouldRenderAddAiNarrativeButton = componentMenu?.useAutomatedNarrative === true;
    const widgetAlreadyHasNarrative = narrativeWidgetsRelationsMap.has(widget.id);
    const selectedWidgetSize = getSizeByDimensions(componentGridSize) ?? ('custom' satisfies WidgetSizes);

    useEffect(() => {
        if (widget.needRefresh && isPayloadReady) {
            setWidgetPayload(widget);
        }
    }, [isPayloadReady]);

    return (
        <PinboardOrReportWidgetWrapperProvider
            value={{
                isGenerativeAi,
                setIsGenerativeAi,
                componentKey: widget.componentKey as keyof IHashmap,
                isLoading,
                onEditWidget: onEdit,
                onToggleNarrative: handleClickNarrative,
                onDeleteWidget: onDelete,
                onWidgetChangeSize: changeWidgetSize,
                className: classes.scrollable,
                selectedWidgetSize,
                isFullscreen: isFullscreen || creatingAutomatedNarrativeIsInProgress,
                reportId,
            }}
        >
            <div
                ref={widgetInReportContainerRef}
                className={`${classes.widgetInReport}`}
                onPointerEnter={() => emitHover(widget.id)}
                onPointerLeave={() => emitHover(null)}
            >
                <div className={classes.content}>
                    {Widget ? (
                        <ErrorBoundary fallback={<WidgetErrorFallback />}>
                            <WidgetDataDevMode widgetId={widget.id} componentKey={widget.componentKey} />
                            <Widget
                                setIsPayloadReady={setIsPayloadReady}
                                widgetViewMode={WidgetView.reportView}
                                widgetSelectedProjectAndContract={
                                    <SelectedProjectContractView
                                        projectName={project?.name}
                                        contractName={contract?.name}
                                        latestVersionTitle={latestVersionTitle}
                                        compareVersionTitle={compareVersionTitle}
                                        componentKey={widget.componentKey}
                                    />
                                }
                                reportWidgetRef={reportWidgetRef}
                                widgetId={widget.id}
                                projectId={widget.projectId}
                                contractId={widget.contractId}
                                latestVersionId={latestVersionId}
                                compareVersionId={compareVersionId}
                                localFilters={widgetState}
                                title={widget.name}
                                externalComponents={[
                                    <WidgetButtonsWrapper key={'buttonsWrapper'}>
                                        {shouldRenderAddAiNarrativeButton && (
                                            <Button
                                                variant={'tertiary'}
                                                startIcon={
                                                    <MuiIcon
                                                        icon={'auto_awesome'}
                                                        fontSize={'16px'}
                                                        color={'inherit'}
                                                    />
                                                }
                                                disabled={widgetAlreadyHasNarrative}
                                                onClick={() => {
                                                    onAddAutomatedNarrative({
                                                        widgetId: widget.id,
                                                        shareTitle: widget.name,
                                                        projectId: widget.projectId,
                                                        contractId: widget.contractId,
                                                    });
                                                }}
                                            >
                                                Add AI narrative
                                            </Button>
                                        )}
                                        <WidgetButton
                                            icon={'edit'}
                                            onClick={onEdit}
                                            tooltipContent={'Edit component'}
                                        />
                                        <ButtonsSeparator />
                                    </WidgetButtonsWrapper>,
                                ]}
                            />
                        </ErrorBoundary>
                    ) : (
                        <NoPermissionWidget NoPermissionMessage={NoPermissionMessage} />
                    )}
                </div>
            </div>
        </PinboardOrReportWidgetWrapperProvider>
    );
};
